import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { PaymentIntent } from '@stripe/stripe-js';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { PaypalCreateOrderRequest, PaypalCreateOrderResponse } from '../models/paypal.model';
import { AdyenHostedResultResponse, CreateAdyenSessionRequest, CreateAdyenSessionResponse } from '../models/adyen.model';
import { AssertPaymentPageSaferpayRequest, AssertPaymentPageSaferpayResponse, InitializeSaferpayPaymentRequest, InitializeSaferpayPaymentResponse } from '../models/saferpay.model';
import { StripSubscriptionDetails } from '../models/stripe-token.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  private http = inject(HttpClient);
  public initiateSaferpaySubject = new Subject<boolean>();
  public handleSaferpayBackgroundTriggerManually = new Subject<boolean>();
  public initiateStripeSubject = new Subject<boolean>();
  // Emitted by parent booking components when /book returns an error so the
  // Stripe component drops its current PaymentIntent and mounts a fresh one —
  // prevents the customer from retrying with an already-charged PI.
  public recreatePaymentIntentSubject = new Subject<void>();
  // Emitted on /book error when PayPal/Saferpay was used. cw-payment-methods
  // clears its hidePaymentOptions + showPaymentSuccessMsg flags so the
  // PayPal button / Saferpay launcher come back into the DOM — the customer
  // can then re-authorize a fresh payment for the retry.
  public resetPaymentSuccessStateSubject = new Subject<void>();
  // Emitted INSTEAD of the reset above when a paid /book lost its answer (connection dropped, 504):
  // the booking may exist, so the payment form must not come back on its own. cw-payment-methods
  // says so and offers "Pay again", which is the customer's explicit decision to re-arm it.
  public bookingOutcomeUnknownSubject = new Subject<void>();
  // Emitted INSTEAD of both when the backend will not claim the payment session again
  // (BACKEND_KEYS.SESSION_ALREADY_USED). Whether a booking stands behind it is not known, so it is
  // handled like a lost answer: "check your email" and an explicit "Pay again", in its own words.
  public paymentSessionSpentSubject = new Subject<void>();
  // Emitted INSTEAD of all of the above when a paid /book was still answered "booking lock" after
  // every resend: the booking is most likely being completed by this payment's own first request.
  // cw-payment-methods says so and offers NO "Pay again" - a second payment is never the answer.
  public bookingStillCompletingSubject = new Subject<void>();
  // Emitted INSTEAD of all of the above when the backend says this payment session already backs a
  // confirmed booking (`params.already_booked = 1`): the booking exists. cw-payment-methods keeps the
  // payment form down and offers NO "Pay again"; the host shows the "already booked" notice.
  public paymentAlreadyBookedSubject = new Subject<void>();
  // True while cw-payment-methods holds the payment form down after any of the above (or waits for
  // "Pay again" where one is offered). The booking views keep Book disabled meanwhile: with the
  // payment form down there is nothing it could book.
  public readonly payAgainRequired = signal(false);

  public InitializeSaferpayPaymentRequest(
    payload: InitializeSaferpayPaymentRequest
  ): Observable<InitializeSaferpayPaymentResponse> {
    return this.http.post<InitializeSaferpayPaymentResponse>(
      environment.apiUrl + 'saferpay/initialize_payment',
      payload
    );
  }

  AssertPaymentPageSaferpayRequest(
    payload: AssertPaymentPageSaferpayRequest
  ): Observable<AssertPaymentPageSaferpayResponse> {
    return this.http.post<AssertPaymentPageSaferpayResponse>(
      environment.apiUrl + 'saferpay/verify_transaction_status',
      payload
    );
  }

  /**
   * Mint an Adyen payment session for the amount about to be charged.
   *
   * The response carries only the session handles and the PUBLIC client key —
   * the API key and HMAC key never leave the backend.
   */
  createAdyenSession(payload: CreateAdyenSessionRequest): Observable<CreateAdyenSessionResponse> {
    return this.http.post<CreateAdyenSessionResponse>(
      environment.apiUrl + 'adyen/sessions',
      payload
    );
  }

  /**
   * The result the Calenso return page stored for a hosted checkout, if any. Read with the nonce AND
   * the token the session answer handed to this window only - the nonce alone travels through the
   * popup and return URLs, so it is not enough to prove who is asking. Anything else answers 404.
   */
  getAdyenHostedResult(nonce: string, token: string): Observable<AdyenHostedResultResponse> {
    return this.http.get<AdyenHostedResultResponse>(
      environment.apiUrl + 'adyen/hosted-result',
      { params: { n: nonce, t: token } }
    );
  }

  createPaymentIntent(subscriptionDetails: StripSubscriptionDetails): Observable<PaymentIntent> {
    return this.http.post<PaymentIntent>(environment.apiUrl + 'payment-intents/create', subscriptionDetails);
  }

  updatePaymentIntent(subscriptionDetails: StripSubscriptionDetails): Observable<PaymentIntent> {
    return this.http.post<PaymentIntent>(environment.apiUrl + 'payment-intents/update', subscriptionDetails);
  }

  createPaypalOrder(details: PaypalCreateOrderRequest): Observable<PaypalCreateOrderResponse> {
    return this.http.post<PaypalCreateOrderResponse>(environment.apiUrl + 'paypal-payments/create-order', details);
  }

  updatePaypalOrder(details: PaypalCreateOrderRequest & { order_id: string; log_uuid: string }): Observable<{ success: boolean; order_id: string }> {
    return this.http.post<{ success: boolean; order_id: string }>(environment.apiUrl + 'paypal-payments/update-order', details);
  }

}
